import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Modal } from 'flowbite';

import SupplierFrameworkAddSupplierModal from './SupplierFrameworkAddSupplierModal';
import SupplierFrameworkTableRow from './SupplierFrameworkTableRow';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TablePagination from 'CommonComponents/TablePagination';
import TableSortHeaderCell from 'CommonComponents/TableSortHeaderCell';

import ExportIcon from 'Assets/icons/export.svg';
import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

export default function SupplierFrameworkTable(props) {
    const [generalSuppliers, setGeneralSuppliers] = useState([]);
    const [updatedSupplier, setUpdatedSupplier] = useState({});
    const [deletedSupplier, setDeletedSupplier] = useState({});
    const [deletedAttachment, setDeletedAttachment] = useState({});

    const [typesList, setTypesList] = useState(props.types);
    const [locationsList, setLocationsList] = useState(props.locations);
    const [areasList, setAreasList] = useState(props.areas);

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [filters, setFilters] = useState({
        types: {},
        locations: {},
        areas: {},
    });

    const [perPage, setPerPage] = useState(20);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);

    const [sortBy, setSortBy] = useState('created_at');
    const [order, setOrder] = useState('desc');
    const [fetchUrl, setFetchUrl] = useState(props.indexUrl);

    const [isLoading, setIsLoading] = useState(false);

    const [supplierFrameworkAddSupplierModalElement, setSupplierFrameworkAddSupplierModalElement] = useState(null);
    const [supplierFrameworkDeleteSupplierModalElement, setSupplierFrameworkDeleteSupplierModalElement] = useState(null);
    const [supplierFrameworkDeleteAttachmentModalElement, setSupplierFrameworkDeleteAttachmentModalElement] = useState(null);

    const supplierFrameworkAddSupplierModalRef = useRef();
    const supplierFrameworkDeleteSupplierModalRef = useRef();
    const supplierFrameworkDeleteAttachmentModalRef = useRef();

    useEffect(() => {
        defaultSetFilters();
        combineUrl();
        if (props.canEdit) {
            supplierFrameworkAddSupplierModalRef &&
                setSupplierFrameworkAddSupplierModalElement(new Modal(supplierFrameworkAddSupplierModalRef.current, modalOptions(false)));
            supplierFrameworkDeleteSupplierModalRef &&
                setSupplierFrameworkDeleteSupplierModalElement(new Modal(supplierFrameworkDeleteSupplierModalRef.current, modalOptions(false)));
            supplierFrameworkDeleteAttachmentModalRef &&
                setSupplierFrameworkDeleteAttachmentModalElement(new Modal(supplierFrameworkDeleteAttachmentModalRef.current, modalOptions(false)));
        }
    }, []);

    useEffect(() => {
        defaultSetFilters();
    }, [typesList, locationsList, areasList]);

    const defaultSetFilters = () => {
        const _filters = JSON.parse(JSON.stringify(filters));

        typesList.forEach((type) => {
            if (!(type in _filters.types)) {
                _filters.types[type] = true;
            }
        });
        locationsList.forEach((location) => {
            if (!(location in _filters.locations)) {
                _filters.locations[location] = true;
            }
        });
        areasList.forEach((area) => {
            if (!(area in _filters.areas)) {
                _filters.areas[area] = true;
            }
        });

        setFilters(_filters);

        return _filters;
    };

    useEffect(() => {
        if (Object.keys(deletedSupplier).length > 0) {
            supplierFrameworkDeleteSupplierModalElement && supplierFrameworkDeleteSupplierModalElement.show();
        }
    }, [deletedSupplier]);

    useEffect(() => {
        if (Object.keys(deletedAttachment).length > 0) {
            supplierFrameworkDeleteAttachmentModalElement && supplierFrameworkDeleteAttachmentModalElement.show();
        }
    }, [deletedAttachment]);

    const fetchData = (url = null, callback = null) => {
        const fetchUrl = url || props.indexUrl;
        setIsLoading(true);
        fetchCommonData();

        const data = {
            filters: defaultSetFilters(),
        };

        axios
            .untranslated()
            .post(fetchUrl, data)
            .then((res) => {
                const { data, links, meta } = res.data;

                setGeneralSuppliers(data);
                setTo(meta.to);
                setFrom(meta.from);
                setTotal(meta.total);
                setLastPage(meta.last_page);

                if (callback) {
                    callback();
                }
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchCommonData = () => {
        axios
            .untranslated()
            .get(props.commonDataUrl)
            .then((res) => {
                const { types, locations, areas } = res.data;
                setTypesList(types);
                setLocationsList(locations);
                setAreasList(areas);
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const combineUrl = (sortByVal = null, orderVal = null, pageVal = null) => {
        const _sort = sortByVal || sortBy;
        const _order = orderVal || order;
        const _page = pageVal || page;

        let url = new URL(fetchUrl);
        let params = new URLSearchParams(url.search);

        params.set('sort_by', '' + _sort);
        params.set('order', '' + _order);
        params.set('page', '' + _page);
        params.set('per_page', '' + perPage);

        let resultUrl = url.origin + url.pathname;

        if (params.toString()) {
            resultUrl += '?' + params.toString();
        }

        setFetchUrl(resultUrl);
        fetchData(resultUrl);
    };

    const pageChange = (data) => {
        const p = data.selected + 1;
        setPage(+data.selected + 1);
        combineUrl(null, null, p);
    };

    const toggleSortBy = (field) => {
        let sortVal = sortBy;
        let orderVal = order;

        if (field === sortBy) {
            orderVal = order === 'asc' ? 'desc' : 'asc';
        } else {
            sortVal = field;
            orderVal = 'asc';
        }

        combineUrl(sortVal, orderVal);
        setSortBy(sortVal);
        setOrder(orderVal);
    };

    const addSupplier = () => {
        supplierFrameworkAddSupplierModalElement && supplierFrameworkAddSupplierModalElement.show();
    };

    const successHandler = () => {
        setUpdatedSupplier({});
        setDeletedSupplier({});
        setDeletedAttachment({});
        combineUrl();
        supplierFrameworkAddSupplierModalElement && supplierFrameworkAddSupplierModalElement.hide();
        supplierFrameworkDeleteSupplierModalElement && supplierFrameworkDeleteSupplierModalElement.hide();
        supplierFrameworkDeleteAttachmentModalElement && supplierFrameworkDeleteAttachmentModalElement.hide();
    };

    const closeModal = () => {
        setUpdatedSupplier({});
        setDeletedSupplier({});
        setDeletedAttachment({});
        supplierFrameworkAddSupplierModalElement && supplierFrameworkAddSupplierModalElement.hide();
        supplierFrameworkDeleteSupplierModalElement && supplierFrameworkDeleteSupplierModalElement.hide();
        supplierFrameworkDeleteAttachmentModalElement && supplierFrameworkDeleteAttachmentModalElement.hide();
    };

    const getDownloadUrl = (id) => {
        return props.downloadUrl.replace('dog', id);
    };

    const download = (e, attachment) => {
        e.preventDefault();
        const url = props.downloadUrl.replace('ATTACHMENT_ID', attachment.id);

        axios({
            url: url,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', attachment.fileName);
            document.body.appendChild(link);
            link.click();
        });
    };

    const deleteSupplier = (supplier) => {
        setDeletedSupplier(supplier);
    };

    const deleteAttachment = (attachment) => {
        setDeletedAttachment(attachment);
    };

    const getUpdateUrl = (supplier) => {
        return props.updateUrl.replace('SUPPLIER_ID', supplier.id);
    };

    const toggleFilter = (group, key) => {
        const _filters = JSON.parse(JSON.stringify(filters));
        _filters[group][key] = !_filters[group][key];

        setFilters(_filters);
    };

    const selectAll = (e, group) => {
        const _filters = JSON.parse(JSON.stringify(filters));
        Object.keys(_filters[group]).forEach((key) => {
            _filters[group][key] = true;
        });

        setFilters(_filters);
    };

    const applyFilters = (e) => {
        combineUrl();
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const triggerDeleteSupplierFramework = () => {
        setActionInProgress(true);

        axios
            .delete(props.deleteUrl.replace('SUPPLIER_ID', deletedSupplier.id), { data: {} })
            .then((response) => {
                setActionCompleted(true);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    const triggerDeleteAttachment = () => {
        setActionInProgress(true);

        let url = props.deleteAttachmentUrl.replace('SUPPLIER_ID', deletedAttachment.general_supplier_id);
        url = url.replace('ATTACHMENT_ID', deletedAttachment.id);

        axios
            .delete(url, { data: {} })
            .then((response) => {
                setActionCompleted(true);
            })
            .catch((e) => {
                setActionFailed(true);
            });
    };

    return (
        <div className="table__card">
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-6">
                <div>
                    <h5 className="table__header">{$t('Supplier Framework')}</h5>
                    <div className="tw-flex tw-items-center tw-space-x-3">
                        <PaginationInfo to={to} from={from} total={total} />
                        <TablePagination lastPage={lastPage} pageChange={pageChange} />
                    </div>
                </div>
                <div className="tw-flex tw-gap-x-1.5">
                    {props.canEdit && (
                        <>
                            <Button variant="primary" onClick={addSupplier}>
                                <PlusCircleIcon className="tw-w-3 tw-h-3" />
                                {$t('Add new Supplier')}
                            </Button>

                            <Button variant="primary" as="a" href={props.importUrl} data-action="navigate">
                                <ExportIcon className="tw-w-3 tw-h-3" />
                                {$t('Import from file')}
                            </Button>
                        </>
                    )}
                    <TableFilterModal id="supplier-framework-table_filter-modal" onApply={applyFilters}>
                        <div className="tw-space-y-4">
                            <div>
                                <div className="tw-flex tw-justify-between tw-mb-2">
                                    <div className="tw-text-xs tw-text-charcoal">{$t('Type')}</div>
                                    <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => selectAll(e, 'types')}>
                                        {$t('Select all')}
                                    </button>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                    {Object.keys(filters.types).map((typeName) => {
                                        return (
                                            <div key={`type_${typeName}`}>
                                                <CheckboxInput
                                                    id={`type_${typeName}`}
                                                    checked={filters.types[typeName]}
                                                    label={typeName}
                                                    onChange={() => toggleFilter('types', typeName)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className="tw-flex tw-justify-between tw-mb-2">
                                    <div className="tw-text-xs tw-text-charcoal">{$t('Office Location')} </div>
                                    <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => selectAll(e, 'locations')}>
                                        {$t('Select all')}
                                    </button>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                    {Object.keys(filters.locations).map((locationName) => {
                                        return (
                                            <div key={`location_${locationName}`}>
                                                <CheckboxInput
                                                    id={`location_${locationName}`}
                                                    checked={filters.locations[locationName]}
                                                    label={locationName}
                                                    onChange={() => toggleFilter('locations', locationName)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <div className="tw-flex tw-justify-between tw-mb-2">
                                    <div className="tw-text-xs tw-text-charcoal">{$t('Area of Operation')} </div>
                                    <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => selectAll(e, 'areas')}>
                                        {$t('Select all')}
                                    </button>
                                </div>
                                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                    {Object.keys(filters.areas).map((areaName) => {
                                        return (
                                            <div key={`area_${areaName}`}>
                                                <CheckboxInput
                                                    id={`area_${areaName}`}
                                                    checked={filters.areas[areaName]}
                                                    label={areaName}
                                                    onChange={() => toggleFilter('areas', areaName)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </TableFilterModal>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr className="tw-cursor-pointer">
                            <th className="tw-min-w-40" onClick={() => toggleSortBy('name')}>
                                <TableSortHeaderCell asc={sortBy === 'name' && order === 'asc'} desc={sortBy === 'name' && order === 'desc'}>
                                    {$t('Supplier')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-min-w-40" onClick={() => toggleSortBy('type')}>
                                <TableSortHeaderCell asc={sortBy === 'type' && order === 'asc'} desc={sortBy === 'type' && order === 'desc'}>
                                    {$t('Supplier Type')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-min-w-40" onClick={() => toggleSortBy('location')}>
                                <TableSortHeaderCell asc={sortBy === 'location' && order === 'asc'} desc={sortBy === 'location' && order === 'desc'}>
                                    {$t('Office Location')}
                                </TableSortHeaderCell>
                            </th>
                            <th className="tw-min-w-40">{$t('Area of Operation')}</th>
                            <th className="tw-min-w-48">{$t('Description of services')}</th>
                            <th className="tw-min-w-40">{$t('Key Contact')}</th>
                            <th className="tw-min-w-40">{$t('Website')}</th>
                            <th className="tw-min-w-40">{$t('Contact Details')}</th>
                            <th className="tw-w-52">{$t('Attachment')}</th>
                            <th className="tw-min-w-40"></th>
                        </tr>
                    </thead>
                    <tbody className="tw-align-top">
                        {generalSuppliers.map((supplier) => {
                            return (
                                <SupplierFrameworkTableRow
                                    key={supplier.id}
                                    supplier={supplier}
                                    download={download}
                                    deleteSupplier={deleteSupplier}
                                    canEdit={props.canEdit}
                                    updateUrl={getUpdateUrl(supplier)}
                                    fetchData={(callback) => fetchData(fetchUrl, callback)}
                                    deleteAttachmentUrl={props.deleteAttachmentUrl}
                                    deleteAttachment={deleteAttachment}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {!!props.canEdit && (
                <SupplierFrameworkAddSupplierModal
                    closeHandler={closeModal}
                    successHandler={successHandler}
                    saveUrl={props.storeUrl}
                    fetchCommonData={fetchCommonData}
                    modalId={'supplier-framework-add-supplier-modal'}
                    modalRef={supplierFrameworkAddSupplierModalRef}
                    modalElement={supplierFrameworkAddSupplierModalElement}
                />
            )}

            {!!props.canEdit && (
                <ConfirmationModal
                    closeHandler={closeModal}
                    successHandler={successHandler}
                    triggerAction={triggerDeleteSupplierFramework}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={Object.keys(deletedSupplier).length > 0}
                    modalId={deletedSupplier?.id}
                    modalRef={supplierFrameworkDeleteSupplierModalRef}
                    modalElement={supplierFrameworkDeleteSupplierModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedSupplier?.name })}
                    actionCompletedMsg={$t('Supplier deleted')}
                    actionFailedMsg={$t('Supplier could not be deleted')}
                    title={$t('Delete supplier')}
                    cancelButtonText="Cancel"
                    primaryButtonText="Delete"
                />
            )}

            {!!props.canEdit && (
                <ConfirmationModal
                    closeHandler={closeModal}
                    successHandler={successHandler}
                    triggerAction={triggerDeleteAttachment}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={Object.keys(deletedAttachment).length > 0}
                    modalId={'delete-general-supplier-attachment'}
                    modalRef={supplierFrameworkDeleteAttachmentModalRef}
                    modalElement={supplierFrameworkDeleteAttachmentModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: deletedAttachment?.fileName })}
                    actionCompletedMsg={$t('Attachment deleted')}
                    actionFailedMsg={$t('Attachment could not be deleted')}
                    title={$t('Delete attachment')}
                    cancelButtonText="Cancel"
                    primaryButtonText="Delete"
                />
            )}
        </div>
    );
}
